import api from './api';
import tesoreriaCajaService, { MovimientoCaja } from './tesoreriaCajaService';
import { getAccessToken } from '../utils/sessionAuthStore';

export interface ChequeCartera {
  nroCarga: number;
  nroBanco: number;
  nombreBanco: string;
  nroSucursal: number;
  nombreSucursal: string;
  nroCheque: number;
  nroLibrador: string;
  librador: string;
  importe: number;
  fechaIngreso: string;
  nroMovCajaIngre: number;
  nroDestino: number;
  fechaSalida: string;
  nroMovCajaSalida: number;
  fechaVencimiento: string;
  concepto: string;
  fechaEmision: string;
  conciliado: number;
  asentado: number;
  mcp: string;
  nroCuenta: string;
  comprobanteIngreso?: string;
  comprobanteSalida?: string;
  senial?: number;
  rechazado?: number;
  /** true = electrónico; false = físico. */
  esElectronico?: boolean;
  fechaRechazo?: string | null;
}

export interface ChequeTerceroRechazoRecargo {
  codigoArticulo: string;
  descripcion: string;
  importe: number;
  /** Solo UI: precio de catálogo al seleccionar el artículo. */
  precioCatalogo?: number;
}

export interface ChequeTerceroRechazoLineaPreview {
  codigoArticulo: string;
  descripcion: string;
  cantidad: number;
  precioUnitario: number;
  porcentajeIva: number;
  importeNeto: number;
  importeIva: number;
  importeTotal: number;
}

export interface ChequeTerceroRechazoPreview {
  nroCarga: number;
  nroCheque: number;
  nroLibrador: string;
  librador: string;
  importeCheque: number;
  origen: 'cartera' | 'depositado' | 'proveedor' | string;
  origenDescripcion: string;
  nroCliente: number;
  nombreCliente?: string;
  cuitCliente?: string;
  tipoIvaCliente?: string;
  idCompania?: string;
  nombreCompania?: string;
  nroSuc: number;
  letra: string;
  requiereCae: boolean;
  advertenciaCae?: string;
  advertenciaSucVenta?: string;
  codigoArticulo: string;
  descripcionArticulo: string;
  porcentajeIva: number;
  lineasNdCliente: ChequeTerceroRechazoLineaPreview[];
  subtotalNdCliente: number;
  ivaNdCliente: number;
  totalNdCliente: number;
  debitoBanco?: {
    nroBanco: number;
    nombreBanco?: string;
    nroCtaCble?: number;
    importe: number;
    leyenda: string;
  } | null;
  ofrecerNdProveedor: boolean;
  ndProveedor?: {
    nroProveedor: number;
    nombreProveedor?: string;
    descripcionLinea: string;
    importe: number;
  } | null;
}

export interface ChequeTerceroRechazoRequest {
  recargos?: ChequeTerceroRechazoRecargo[];
  generarNdProveedor?: boolean;
  emitirConCaePrimero?: boolean;
  guardarSinCae?: boolean;
  idCompania?: string;
}

export interface ChequeTerceroRechazoResult {
  exitoso: boolean;
  mensaje?: string;
  requiereCae?: boolean;
  caeObtenido?: boolean;
  ndClienteGrabada?: boolean;
  caeError?: string;
  nroCarga: number;
  nroFormularioNdCliente?: number;
  letraNdCliente?: string;
  nroSucNdCliente?: number;
  nroComprobanteNdCliente?: number;
  cae?: string;
  nroFormularioDebitoBanco?: number;
  nroFormularioNdProveedor?: number;
}

export interface ChequeTerceroRechazoDocumento {
  tipo: string;
  titulo: string;
  nroFormulario: number;
  comprobante?: string | null;
  importe?: number | null;
  fecha?: string | null;
  nombreTercero?: string | null;
  cae?: string | null;
}

export interface ChequeTerceroRechazoDocumentos {
  nroCarga: number;
  fechaRechazo?: string | null;
  documentos: ChequeTerceroRechazoDocumento[];
}

export interface CuponCartera {
  nroCarga: number;
  codigoMarca: number;
  nombreMarca?: string;
  nroTarjeta: string;
  numeroroAutorizacion: string;
  nroComprobanteTa: number;
  fecha?: string;
  fechaPresentar?: string;
  titularTarjeta?: string;
  telefono?: string;
  montoOperacion: number;
  nroDestino?: number;
  mcp?: string;
  nroIdformulario?: number;
  nroMovCaja?: number;
  nroMovCajaSalida?: number;
  comprobanteIngreso?: string;
  comprobanteSalida?: string;
}

/** Parámetros opcionales de GET /api/Tesoreria/tarjetas-cartera. */
export interface TarjetasCarteraQueryParams {
  searchTerm?: string;
  fechaDesde?: string;
  fechaHasta?: string;
  fechaPresentarDesde?: string;
  fechaPresentarHasta?: string;
  codigoMarca?: number;
  importeDesde?: number;
  importeHasta?: number;
  mcp?: string;
}

/** Dirección de ordenamiento (query SortDir). */
export type ChequesSortDir = 'asc' | 'desc';

/**
 * Campos SortBy para GET cheques-cartera.
 * Debe coincidir con el whitelist del backend (ver docs/plan-ordenamiento-cheques-backend.md).
 */
export type ChequesCarteraSortBy =
  | 'librador'
  | 'importe'
  | 'fechaVencimiento'
  | 'fechaEmision'
  | 'fechaIngreso'
  | 'fechaSalida'
  | 'nroCheque'
  | 'nombreBanco'
  | 'nombreSucursal'
  | 'nroLibrador'
  | 'mcp'
  | 'comprobanteIngreso'
  | 'comprobanteSalida';

/**
 * Campos SortBy para GET cheques-propios y cheques-propios-pendientes-acreditar.
 * Excluye `concepto` a pedido de producto.
 */
export type ChequesPropiosSortBy =
  | 'nroCheque'
  | 'nombreBanco'
  | 'nombreSucursal'
  | 'nroLibrador'
  | 'librador'
  | 'importe'
  | 'fechaIngreso'
  | 'fechaVencimiento'
  | 'nroCuenta'
  | 'nroBanco'
  | 'nroSucursal'
  | 'destino'
  | 'fechaAcred';

/** Parámetros opcionales de GET /api/Tesoreria/cheques-cartera (alineado con ChequesCarteraSearchDto). */
export interface ChequesCarteraQueryParams {
  searchTerm?: string;
  fechaDesde?: string;
  fechaHasta?: string;
  fechaVencimientoDesde?: string;
  fechaVencimientoHasta?: string;
  nroBanco?: number;
  nroSucursal?: number;
  importeDesde?: number;
  importeHasta?: number;
  mcp?: string;
  estado?: string;
  librador?: string;
  /** Campo de ordenamiento (server-side). */
  sortBy?: ChequesCarteraSortBy;
  /** Dirección: asc | desc. Default backend sugerido: asc. */
  sortDir?: ChequesSortDir;
}

/**
 * Contrato cheques propios (consulta / acreditar / anular) — fuente de verdad para backend.
 *
 * Listado: GET /api/Tesoreria/cheques-propios
 *   Query: SearchTerm, PageNumber, PageSize, FechaDesde, FechaHasta,
 *          FechaVencimientoDesde, FechaVencimientoHasta, NroBanco, NroSucursal,
 *          Librador, CodigoCaja,
 *          estado?: 'pendientes' | 'acreditados' | 'anulados' | 'todos',
 *          SortBy?: ChequesPropiosSortBy, SortDir?: 'asc' | 'desc'
 *   Semántica estado (LibroBanco):
 *     pendientes  → baja = 0 y no anulado
 *     acreditados → con fechaAcred / baja acreditada
 *     anulados    → anulado = 1
 *     todos       → sin filtro de estado
 *
 * Acreditar: POST /api/Tesoreria/cheques-propios/acreditar
 *   Body: { nroCargas: number[]; fechaAcred: string } // YYYY-MM-DD
 *
 * Anular: POST /api/Tesoreria/cheques-propios/anular
 *   Body: { nroCargas: number[]; motivo?: string | null }
 *
 * Fallback listado pendientes: GET /api/Tesoreria/cheques-propios-pendientes-acreditar
 *   (mismos SortBy / SortDir que cheques-propios)
 */
export type ChequePropioEstadoFiltro = 'pendientes' | 'acreditados' | 'anulados' | 'todos';

export interface ChequePropio {
  nroCarga: number;
  nroBanco: number;
  nombreBanco: string;
  nroSucursal: number;
  nombreSucursal: string;
  nroCheque: number;
  nroLibrador: number;
  librador: string;
  importe: number;
  fechaIngreso: string;
  nroDestino: number;
  destino: string | null;
  fechaVencimiento: string;
  concepto: string;
  nroCuenta: string | null;
  fechaAcred: string | null;
  baja: number | null;
  anulado: number | null;
}

export interface ChequesPropiosQueryParams {
  searchTerm?: string;
  pageNumber?: number;
  pageSize?: number;
  fechaDesde?: string;
  fechaHasta?: string;
  fechaVencimientoDesde?: string;
  fechaVencimientoHasta?: string;
  nroBanco?: number;
  nroSucursal?: number;
  librador?: string;
  codigoCaja?: number;
  /** Preferido en GET cheques-propios; si backend aún no lo soporta, el front usa fallback. */
  estado?: ChequePropioEstadoFiltro;
  /** Campo de ordenamiento (server-side). */
  sortBy?: ChequesPropiosSortBy;
  /** Dirección: asc | desc. Default backend sugerido: asc. */
  sortDir?: ChequesSortDir;
}

export interface ChequesPropiosPagedResult {
  chequesPropios: ChequePropio[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages?: number;
}

export interface AcreditarChequesPropiosPayload {
  nroCargas: number[];
  /** Fecha de acreditación ISO date YYYY-MM-DD */
  fechaAcred: string;
}

export interface AnularChequesPropiosPayload {
  nroCargas: number[];
  motivo?: string | null;
}

export interface GrupoCajaChica {
  id: number;
  nombre: string;
  codigo: number;
  grupoMcpDestino?: number;
}

export interface Caja {
  nroCaja?: number;
  codigo?: number;
  grupo?: number;
  estado?: string;
  [key: string]: any;
}

export interface GrupoMcpDetalle {
  grupoMcp: number;
  descripcion: string;
  grupoMcpDestino?: number;
}

export interface MedioTransferencia {
  id: number;
  nombre: string; // opcional en UI, mantenemos por compatibilidad
  descripcion?: string; // API: descripcion
  mcp?: string;
  clase?: string;
  // Campos adicionales para derivaciones de payload
  grupo?: number;
  nroSuc?: number;
  tipo?: string;
  cartera?: number;
  nombreBanco?: string;
  cbu?: string;
  nroDeCuenta?: string;
  /** Compañía de la cartera asociada al MCP (si tiene). Vacío = sin filtro. */
  idCompania?: string;
}

export interface MarcaTarjeta {
  /** Código de marca en BD (MarcasTarjetas.CodigoMarca). */
  codigoMarca: number;
  /** Alias de codigoMarca para compatibilidad con selects legacy. */
  id: number;
  nombre: string;
  nombreMarca?: string;
}

export interface Banco {
  id: number;
  nombre: string;
  codigo?: number | string;
  nroBanco?: number | string;
}

/** Payload alineado a ReciboCreateDto (POST /api/Tesoreria/recibos). */
export interface ReciboChequeDetalleCreate {
  nroBanco: number;
  nroSucursal: number;
  nroCheque: string;
  nroLibrador: string;
  librador: string;
  fechaEmision?: string;
  fechaVencimiento?: string;
  ctaBancaria?: string;
  concepto?: string;
}

export interface ReciboTarjetaDetalleCreate {
  codigoMarca: number;
  nroTarjeta?: string;
  nroComprobanteTa?: number;
  fecha?: string;
  fechaPresentar?: string;
  numeroroAutorizacion?: string;
  cuotas?: number;
  telefono?: string;
  titularTarjeta?: string;
}

export interface ReciboMedioCobroCreate {
  mcp: string;
  importe: number;
  detalle?: string;
  cheque?: ReciboChequeDetalleCreate;
  tarjeta?: ReciboTarjetaDetalleCreate;
  cartera?: number;
  /** Fecha de registro del comprobante (solo medios tipo Otros). */
  fecha?: string;
  /** Número de comprobante/certificado (solo medios tipo Otros). */
  nroComprobante?: number;
}

/** Campos opcionales de medios Otros → payload de creación de recibo. */
export function appendMedioOtrosApiFields(
  base: ReciboMedioCobroCreate,
  opts: { fechaRegistro?: string; nroComprobante?: number },
): ReciboMedioCobroCreate {
  const fr = (opts.fechaRegistro || '').trim();
  if (fr) {
    base.fecha = new Date(`${fr}T12:00:00`).toISOString();
  }
  const nro = Number(opts.nroComprobante);
  if (Number.isFinite(nro) && nro > 0) {
    base.nroComprobante = Math.trunc(nro);
  }
  return base;
}

export interface ReciboDocumentoAplicarCreate {
  nroIdformulario: number;
  montoCancelado: number;
}

export interface ReciboCreatePayload {
  nroCliente: number;
  nroSuc: number;
  grupoMCP: number;
  fechaEmision?: string;
  descripcion?: string;
  /** Cabecera del recibo; obligatorio si SucVenta.numPropio = M (numeración manual). */
  nroComprobante?: number;
  mediosDeCobro: ReciboMedioCobroCreate[];
  documentosAplicar?: ReciboDocumentoAplicarCreate[];
}

export interface ReciboCreateResponse {
  nroFormulario?: number;
  letra?: string;
  nroSuc?: number;
  nroComprobante?: number;
  importeTotal?: number;
  [key: string]: unknown;
}

export interface OrdenPagoMedioDto {
  mcp?: string;
  tipoPago?: string;
  importe: number;
  detalle?: string;
  nroCheque?: string;
  librador?: string;
  nombreBanco?: string;
  nroBanco?: number;
  fechaVencimiento?: string;
  cartera?: number;
}

export interface OrdenPagoDocumentoAplicadoDto {
  nroIdformulario: number;
  nroTipoFormulario: string;
  letra: string;
  nroSuc?: number;
  nroComprobante: number;
  fechaEmision: string;
  importeTotal?: number;
  montoCancelado?: number;
  nroMovCaja: number;
}

export interface OrdenPagoEnvioEmailPreview {
  emailsSugeridos: string[];
  asuntoSugerido?: string | null;
  mensajeSugerido?: string | null;
  emailEnviadoAnteriormente?: string | null;
  smtpConfigurado: boolean;
  remitenteEmail?: string | null;
  remitenteNombre?: string | null;
  nombreProveedor?: string | null;
  comprobanteLabel?: string | null;
}

export interface OrdenPagoEnvioEmailRequest {
  destinatarios: string[];
  cc?: string[];
  asunto?: string;
  mensaje?: string;
}

export interface OrdenPagoEnvioEmailResult {
  enviado: boolean;
  mensaje?: string | null;
  emailEnvioRegistrado?: string | null;
}

export interface OrdenPagoDetalleDto {
  nroFormulario: number;
  fechaEmision: string;
  nroProveedor: number;
  nombre: string;
  importeTotal: number;
  formaDePago: string;
  usuario: string;
  tipoComprobante: string;
  nroSuc: number;
  descripcion: string;
  cuit: string;
  letra?: string;
  nroComprobante?: number;
  nroAsiento?: number | null;
  asentado?: string;
  codigoCaja?: number | null;
  puedeAnular?: boolean;
  motivoAnulacion?: string | null;
  mediosDePago: OrdenPagoMedioDto[];
  documentosAplicados: OrdenPagoDocumentoAplicadoDto[];
}

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
};

const asRecordArray = (value: unknown): Record<string, unknown>[] => {
  if (typeof value === 'string') {
    try {
      return asRecordArray(JSON.parse(value) as unknown);
    } catch {
      return [];
    }
  }
  if (Array.isArray(value)) return value as Record<string, unknown>[];
  const obj = asRecord(value);
  if (!obj) return [];
  const nested = obj.items ?? obj.Items ?? obj.data ?? obj.Data;
  return Array.isArray(nested) ? (nested as Record<string, unknown>[]) : [];
};

const coercePositiveInt = (raw: unknown): number | null => {
  if (raw == null || raw === '') return null;
  const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const pickFirstArray = (...candidates: unknown[]): Record<string, unknown>[] => {
  for (const candidate of candidates) {
    const arr = asRecordArray(candidate);
    if (arr.length > 0) return arr;
  }
  return [];
};

const mergeRecordsPreferringFilled = (
  ...parts: Array<Record<string, unknown> | null | undefined>
): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const part of parts) {
    if (!part) continue;
    for (const [key, value] of Object.entries(part)) {
      const prev = out[key];
      if (Array.isArray(value)) {
        if (value.length > 0) {
          out[key] = value;
        } else if (!(key in out)) {
          out[key] = value;
        }
        continue;
      }
      if (value == null || value === '') {
        if (!(key in out)) out[key] = value;
        continue;
      }
      if (Array.isArray(prev) && prev.length > 0) continue;
      out[key] = value;
    }
  }
  return out;
};

const unwrapOrdenPagoPayload = (data: unknown, id: number): Record<string, unknown> => {
  const obj = asRecord(data);
  if (!obj) return {};
  const nested =
    asRecord(obj.data) ??
    asRecord(obj.Data) ??
    asRecord(obj.ordenPago) ??
    asRecord(obj.OrdenPago) ??
    asRecord(obj.detalle) ??
    asRecord(obj.Detalle) ??
    asRecord(obj.item) ??
    asRecord(obj.Item);
  const list = pickFirstArray(obj.items, obj.Items, obj);
  const found = list.find((item) => coercePositiveInt(item.nroFormulario ?? item.NroFormulario) === id);
  return mergeRecordsPreferringFilled(obj, nested, found);
};

const mapOrdenPagoMedioDto = (raw: Record<string, unknown>): OrdenPagoMedioDto => {
  const cheque = asRecord(raw.cheque) ?? asRecord(raw.Cheque) ?? {};
  const importeRaw =
    raw.importe ??
    raw.Importe ??
    raw.monto ??
    raw.Monto ??
    raw.haber ??
    raw.Haber ??
    raw.egreso ??
    raw.Egreso ??
    raw.importeMovimiento ??
    raw.ImporteMovimiento ??
    0;
  return {
    mcp:
      String(raw.mcp ?? raw.MCP ?? raw.nombreMCP ?? raw.NombreMCP ?? raw.nombreMcp ?? raw.NombreMcp ?? '').trim() ||
      undefined,
    tipoPago:
      String(
        raw.tipoPago ?? raw.TipoPago ?? raw.nombreTipoPago ?? raw.NombreTipoPago ?? raw.tipo ?? raw.Tipo ?? ''
      ).trim() || undefined,
    importe: Math.abs(Number(importeRaw) || 0),
    detalle: String(raw.detalle ?? raw.Detalle ?? raw.observaciones ?? raw.Observaciones ?? '').trim() || undefined,
    nroCheque:
      String(cheque.nroCheque ?? cheque.NroCheque ?? raw.nroCheque ?? raw.NroCheque ?? raw.nroChequeCheque ?? '').trim() ||
      undefined,
    librador: String(cheque.librador ?? cheque.Librador ?? raw.librador ?? raw.Librador ?? '').trim() || undefined,
    nombreBanco:
      String(
        cheque.nombreBanco ??
          raw.nombreBanco ??
          raw.NombreBanco ??
          raw.nombreBancoCheque ??
          raw.NombreBancoCheque ??
          ''
      ).trim() || undefined,
    nroBanco: coercePositiveInt(cheque.nroBanco ?? raw.nroBanco ?? raw.NroBanco) ?? undefined,
    fechaVencimiento:
      String(cheque.fechaVencimiento ?? raw.fechaVencimiento ?? raw.FechaVencimiento ?? '').trim() || undefined,
    cartera: coercePositiveInt(raw.cartera ?? raw.Cartera) ?? undefined,
  };
};

const looksLikeMedioPago = (raw: Record<string, unknown>): boolean => {
  const mapped = mapOrdenPagoMedioDto(raw);
  return Boolean(mapped.mcp || mapped.tipoPago || mapped.nroCheque || mapped.importe);
};

const mapOrdenPagoDocumentoAplicadoDto = (raw: Record<string, unknown>): OrdenPagoDocumentoAplicadoDto => ({
  nroIdformulario:
    coercePositiveInt(
      raw.nroIdformulario ??
        raw.NroIdformulario ??
        raw.nroIdFormulario ??
        raw.NroIdFormulario ??
        raw.nroFormulario ??
        raw.NroFormulario
    ) ?? 0,
  nroTipoFormulario: String(raw.nroTipoFormulario ?? raw.NroTipoFormulario ?? '').trim(),
  letra: String(raw.letra ?? raw.Letra ?? ''),
  nroSuc: Number(raw.nroSuc ?? raw.NroSuc ?? 0) || undefined,
  nroComprobante: Number(raw.nroComprobante ?? raw.NroComprobante ?? 0) || 0,
  fechaEmision: String(raw.fechaEmision ?? raw.FechaEmision ?? raw.fecha ?? raw.Fecha ?? ''),
  importeTotal: Number(raw.importeTotal ?? raw.ImporteTotal ?? 0) || undefined,
  montoCancelado: Number(raw.montoCancelado ?? raw.MontoCancelado ?? raw.importe ?? raw.Importe ?? 0) || undefined,
  nroMovCaja: Number(raw.nroMovCaja ?? raw.NroMovCaja ?? 0),
});

const pickMediosOrdenPago = (item: Record<string, unknown>): OrdenPagoMedioDto[] => {
  const specific = pickFirstArray(
    item.mediosDePago,
    item.MediosDePago,
    item.mediosDeCobro,
    item.MediosDeCobro,
    item.detalleMedios,
    item.DetalleMedios,
    item.detallesPago,
    item.DetallesPago,
    item.lineasPago,
    item.LineasPago,
    item.movimientosCaja,
    item.MovimientosCaja,
    item.medios,
    item.Medios
  );
  if (specific.length > 0) return specific.map(mapOrdenPagoMedioDto);

  const generic = pickFirstArray(item.detalles, item.Detalles, item.movimientos, item.Movimientos).filter(
    looksLikeMedioPago
  );
  return generic.map(mapOrdenPagoMedioDto);
};

const pickDocumentosOrdenPago = (item: Record<string, unknown>): OrdenPagoDocumentoAplicadoDto[] =>
  pickFirstArray(
    item.documentosAplicar,
    item.DocumentosAplicar,
    item.documentosAplicados,
    item.DocumentosAplicados,
    item.aplicaciones,
    item.Aplicaciones,
    item.imputaciones,
    item.Imputaciones
  )
    .map(mapOrdenPagoDocumentoAplicadoDto)
    .filter((doc) => doc.nroIdformulario > 0 || (doc.montoCancelado ?? 0) > 0);

const mapOrdenPagoDetalleFromItem = (item: Record<string, unknown>): OrdenPagoDetalleDto => {
  const asentado = String(item.asentado ?? item.Asentado ?? '');
  const nombre = String(item.nombre ?? item.Nombre ?? '');
  const anulada = asentado.trim().toUpperCase() === 'A' || nombre.trim().toUpperCase() === 'ANULADO';
  const puedeAnularRaw = item.puedeAnular ?? item.PuedeAnular;

  return {
    nroFormulario: coercePositiveInt(item.nroFormulario ?? item.NroFormulario) ?? 0,
    fechaEmision: String(item.fechaEmision ?? item.FechaEmision ?? ''),
    nroProveedor: Number(item.nroProveedor ?? item.NroProveedor ?? item.nroCliente ?? item.NroCliente ?? 0) || 0,
    nombre,
    importeTotal: Number(item.importeTotal ?? item.ImporteTotal ?? 0) || 0,
    formaDePago: String(item.formaDePago ?? item.FormaDePago ?? ''),
    usuario: String(item.usuario ?? item.Usuario ?? ''),
    tipoComprobante: String(item.tipoComprobante ?? item.nroTipoFormulario ?? item.NroTipoFormulario ?? ''),
    nroSuc: Number(item.nroSuc ?? item.NroSuc ?? 0) || 0,
    descripcion: String(item.descripcion ?? item.Descripcion ?? ''),
    cuit: String(item.cuit ?? item.CUIT ?? ''),
    letra: String(item.letra ?? item.Letra ?? '') || undefined,
    nroComprobante: Number(item.nroComprobante ?? item.NroComprobante ?? 0) || undefined,
    nroAsiento: coercePositiveInt(item.nroAsiento ?? item.NroAsiento),
    asentado,
    codigoCaja: coercePositiveInt(item.codigoCaja ?? item.CodigoCaja) ?? null,
    puedeAnular: anulada ? false : puedeAnularRaw == null ? true : Boolean(puedeAnularRaw),
    motivoAnulacion: (item.motivoAnulacion ?? item.MotivoAnulacion) as string | null,
    mediosDePago: pickMediosOrdenPago(item),
    documentosAplicados: pickDocumentosOrdenPago(item),
  };
};

const mapOrdenPagoDetalleDto = (data: unknown, id: number): OrdenPagoDetalleDto =>
  mapOrdenPagoDetalleFromItem(unwrapOrdenPagoPayload(data, id));

const toFechaYmd = (value: string): string | undefined => {
  const match = String(value || '').match(/^(\d{4}-\d{2}-\d{2})/);
  return match?.[1];
};

const mapMovimientoCajaToMedio = (mov: MovimientoCaja): OrdenPagoMedioDto => ({
  mcp: String(mov.nombreMCP || mov.mcp || '').trim() || undefined,
  tipoPago: String(mov.tipoPago || '').trim() || undefined,
  importe: Math.abs(Number(mov.importe) || 0),
  detalle: String(mov.detalle || '').trim() || undefined,
  nroCheque: mov.nroCheque != null ? String(mov.nroCheque) : undefined,
  librador: String(mov.nroLibrador || '').trim() || undefined,
  nombreBanco: String(mov.nombreBancoCheque || '').trim() || undefined,
  nroBanco: mov.nroBancoCheque ?? undefined,
  cartera: mov.cartera || undefined,
});

const enrichMediosFromCaja = async (
  mapped: OrdenPagoDetalleDto,
  id: number
): Promise<OrdenPagoDetalleDto> => {
  if (mapped.mediosDePago.length > 0) return mapped;

  try {
    const response = await api.get(`/api/TesoreriaCaja/movimientos-caja/${id}`);
    const fromMov = asRecordArray(response.data)
      .filter((item) => (coercePositiveInt(item.nroFormulario ?? item.NroFormulario) ?? id) === id)
      .filter(looksLikeMedioPago)
      .map(mapOrdenPagoMedioDto);
    if (fromMov.length > 0) return { ...mapped, mediosDePago: fromMov };
  } catch {
    // El endpoint de movimiento por id puede no existir; se intenta por caja.
  }

  const codigoCaja = mapped.codigoCaja;
  if (!codigoCaja || codigoCaja <= 0) return mapped;

  try {
    const fecha = toFechaYmd(mapped.fechaEmision);
    const found: MovimientoCaja[] = [];
    for (let page = 1; page <= 10; page++) {
      const res = await tesoreriaCajaService.getDetalleCaja(
        codigoCaja,
        page,
        100,
        'Egreso',
        fecha,
        fecha
      );
      const items = res.items ?? [];
      found.push(...items.filter((mov) => mov.nroFormulario === id));
      if (items.length < 100) break;
      if (res.totalCount != null && page * 100 >= res.totalCount) break;
    }
    if (found.length === 0) return mapped;
    return { ...mapped, mediosDePago: found.map(mapMovimientoCajaToMedio) };
  } catch {
    return mapped;
  }
};

export interface GetGruposCajaOptions {
  usuario?: string;
  nroCliente?: number;
}
export interface GetGruposCajaPagedOptions extends GetGruposCajaOptions {
  pageNumber: number;
  pageSize: number;
}

const tesoreriaService = {
  getGruposCajaChica: async (options?: GetGruposCajaOptions): Promise<GrupoCajaChica[]> => {
    const token = getAccessToken();
    const storedUser =
      options?.usuario ||
      localStorage.getItem('userName') ||
      localStorage.getItem('username') ||
      localStorage.getItem('user') ||
      undefined;
    const response = await api.get<any>('/api/GrupoMcpUsuario', {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params: {
        pageNumber: 1,
        pageSize: 50, // Límite del backend para GrupoMcpUsuario
        ...(options?.nroCliente ? { nroCliente: options.nroCliente } : {}),
        ...(storedUser ? { usuario: storedUser, Usuario: storedUser } : {}),
      },
    });
    const data = response.data;

    const rawItems: any[] = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];

    const mapped: GrupoCajaChica[] = rawItems.map((it: any) => {
      const grupo = it?.grupoMcp ?? it?.grupo ?? it?.id ?? 0;
      return {
        id: grupo,
        nombre: `Grupo MCP ${grupo}`,
        codigo: grupo,
      };
    });

    // Eliminar duplicados por id/código
    const uniqueById = new Map<number, GrupoCajaChica>();
    for (const item of mapped) {
      if (!uniqueById.has(item.id)) {
        uniqueById.set(item.id, item);
      }
    }

    // Enriquecer con descripción real de cada grupo
    const gruposUnicos = Array.from(uniqueById.keys());
    try {
      const detalles = await Promise.all(
        gruposUnicos.map(async (gid) => {
          const detResp = await api.get<GrupoMcpDetalle>(`/api/GrupoMcp/${gid}`, {
            headers: {
              Authorization: token ? `Bearer ${token}` : undefined,
            },
          });
          return detResp.data;
        })
      );

      for (const det of detalles) {
        const current = uniqueById.get(det.grupoMcp);
        if (current) {
          const grupoMcpDestino = det.grupoMcpDestino ?? (det as { GrupoMcpDestino?: number }).GrupoMcpDestino;
          uniqueById.set(det.grupoMcp, {
            ...current,
            nombre: det.descripcion || current.nombre,
            ...(grupoMcpDestino != null && grupoMcpDestino > 0 ? { grupoMcpDestino } : {}),
          });
        }
      }
    } catch (err) {
      // Si falla la descripción, devolvemos los nombres por defecto
      console.warn('No se pudieron cargar descripciones de Grupo MCP', err);
    }

    return Array.from(uniqueById.values());
  },

  getGruposCajaChicaPaged: async (options: GetGruposCajaPagedOptions): Promise<GrupoCajaChica[]> => {
    const token = getAccessToken();
    const storedUser =
      options?.usuario ||
      localStorage.getItem('userName') ||
      localStorage.getItem('username') ||
      localStorage.getItem('user') ||
      undefined;
    const response = await api.get<any>('/api/GrupoMcpUsuario', {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params: {
        pageNumber: options.pageNumber,
        pageSize: options.pageSize,
        ...(options?.nroCliente ? { nroCliente: options.nroCliente } : {}),
        ...(storedUser ? { usuario: storedUser, Usuario: storedUser } : {}),
      },
    });
    const data = response.data;

    const rawItems: any[] = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];

    const mapped: GrupoCajaChica[] = rawItems.map((it: any) => {
      const grupo = it?.grupoMcp ?? it?.grupo ?? it?.id ?? 0;
      return {
        id: grupo,
        nombre: `Grupo MCP ${grupo}`,
        codigo: grupo,
      };
    });

    // Enriquecer con descripción real de cada grupo (solo de esta página)
    try {
      const detalles = await Promise.all(
        mapped.map(async (g) => {
          const detResp = await api.get<GrupoMcpDetalle>(`/api/GrupoMcp/${g.id}`, {
            headers: {
              Authorization: token ? `Bearer ${token}` : undefined,
            },
          });
          return detResp.data;
        })
      );
      const byId = new Map<number, GrupoCajaChica>(mapped.map(m => [m.id, m]));
      for (const det of detalles) {
        const current = byId.get(det.grupoMcp);
        if (current) {
          const grupoMcpDestino = det.grupoMcpDestino ?? (det as { GrupoMcpDestino?: number }).GrupoMcpDestino;
          byId.set(det.grupoMcp, {
            ...current,
            nombre: det.descripcion || current.nombre,
            ...(grupoMcpDestino != null && grupoMcpDestino > 0 ? { grupoMcpDestino } : {}),
          });
        }
      }
      return Array.from(byId.values());
    } catch (err) {
      console.warn('No se pudieron cargar descripciones de Grupo MCP (paginado)', err);
      return mapped;
    }
  },

  /** Obtiene TODOS los MCPs por tipo desde GET /api/Mcps?Tipo=X, paginando todas las páginas. */
  getMcpsByTipo: async (tipo: string): Promise<MedioTransferencia[]> => {
    const tipoNorm = (tipo ?? '').toString().trim().toUpperCase();
    if (!tipoNorm) return [];
    const token = getAccessToken();
    const mapItem = (it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.nroCuenta ?? it?.cartera ?? idx,
      nombre: it?.banco ?? it?.nombre ?? it?.descripcion ?? it?.detalle ?? `Medio ${idx + 1}`,
      descripcion: it?.descripcion ?? it?.Detalle ?? undefined,
      mcp: it?.mcp ?? it?.MCP ?? undefined,
      clase: it?.clase ?? it?.Clase ?? undefined,
      grupo: it?.grupoMcp ?? it?.grupo ?? undefined,
      nroSuc: it?.nroSuc ?? it?.sucursal ?? undefined,
      tipo: (it?.tipo ?? it?.Tipo ?? '') ? String(it.tipo ?? it.Tipo).trim().toUpperCase() : undefined,
    });
    const allItems: MedioTransferencia[] = [];
    let pageNumber = 1;
    /** Debe coincidir con MaxPageSize del API (McpsController); si no, la paginación corta en la primera página. */
    const pageSize = 50;
    let globalIdx = 0;
    for (let i = 0; i < 200; i++) {
      const response = await api.get<any>('/api/Mcps', {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
        params: { Tipo: tipoNorm, PageNumber: pageNumber, PageSize: pageSize },
      });
      const data = response.data;
      const items: any[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.value)
        ? data.value
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.data?.items)
        ? data.data.items
        : [];
      if (items.length === 0) break;
      for (const it of items) { allItems.push(mapItem(it, globalIdx++)); }
      if (items.length < pageSize) break;
      pageNumber += 1;
    }
    return allItems;
  },

  /** Obtiene TODAS las chequeras desde GET /api/Mcps (todas las páginas). */
  getMcpsAll: async (): Promise<MedioTransferencia[]> => {
    const token = getAccessToken();
    const mapItem = (it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.nroCuenta ?? it?.cartera ?? idx,
      nombre: it?.banco ?? it?.nombre ?? it?.descripcion ?? it?.detalle ?? `Medio ${idx + 1}`,
      descripcion: it?.descripcion ?? it?.Detalle ?? undefined,
      mcp: it?.mcp ?? it?.MCP ?? undefined,
      clase: it?.clase ?? it?.Clase ?? undefined,
      grupo: it?.grupoMcp ?? it?.grupo ?? undefined,
      nroSuc: it?.nroSuc ?? it?.sucursal ?? undefined,
      tipo: (it?.tipo ?? it?.Tipo ?? '') ? String(it.tipo ?? it.Tipo).trim().toUpperCase() : undefined,
    });
    const allItems: MedioTransferencia[] = [];
    let pageNumber = 1;
    const pageSize = 50;
    for (let i = 0; i < 200; i++) {
      const response = await api.get<any>('/api/Mcps', {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
        params: { PageNumber: pageNumber, PageSize: pageSize },
      });
      const data = response.data;
      const items: any[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.value)
        ? data.value
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.data?.items)
        ? data.data.items
        : [];
      if (items.length === 0) break;
      allItems.push(...items.map(mapItem));
      if (items.length < pageSize) break;
      pageNumber += 1;
    }
    return allItems;
  },

  /** Obtiene TODAS las chequeras de todos los grupos. Intenta GET /api/Mcps; si falla, usa por-grupo. */
  getMcpsPorTipo: async (tipoBuscado: string, grupoActual?: number): Promise<MedioTransferencia[]> => {
    const tipoNorm = (tipoBuscado ?? '').toString().trim().toUpperCase();
    if (!tipoNorm) return [];

    let todos: MedioTransferencia[] = await tesoreriaService.getMcpsAll().catch(() => []);
    if (todos.length === 0) {
      const gruposList = await tesoreriaService.getGruposCajaChica().catch(() => []);
      const grupos = gruposList.length > 0 ? gruposList : (grupoActual != null ? [{ id: grupoActual, nombre: '', codigo: grupoActual }] : []);
      if (grupos.length > 0) {
        const results = await Promise.all(grupos.map((g) => tesoreriaService.getMcpsPorGrupo(g.codigo).catch(() => [] as MedioTransferencia[])));
        todos = results.flat();
      }
    }
    const filtrados = todos.filter((m) => {
      const mTipo = (m.tipo ?? '').toString().trim().toUpperCase();
      const mClase = (m.clase ?? '').toString().trim().toUpperCase();
      const mDesc = (m.descripcion ?? m.nombre ?? '').toString().toUpperCase();
      const mMcp = (m.mcp ?? '').toString().toUpperCase();
      const mFirst = mTipo.charAt(0);
      if (tipoNorm === 'E') {
        if (mFirst === 'P') return false;
        return mTipo === 'E' || mFirst === 'E' || mClase.includes('EFECTIVO') || mDesc.includes('EFECTIVO') || mMcp.includes('EFECTIVO');
      }
      if (tipoNorm === 'P' || tipoNorm === 'C') {
        if (mFirst === 'E') return false;
        return mTipo === tipoNorm || mFirst === tipoNorm || mClase.includes('CHEQUE') || mDesc.includes('CHEQUE') || mMcp.includes('CHEQUE');
      }
      if (tipoNorm === 'T') {
        return mFirst === 'T' || mClase.includes('TARJETA') || mDesc.includes('TARJ') || mMcp.includes('TARJ');
      }
      return mTipo === tipoNorm || mFirst === tipoNorm;
    });
    const byMcp = new Map<string, MedioTransferencia>();
    for (const m of filtrados) {
      const key = (m.mcp ?? '').trim() || `${m.grupo}-${m.id}`;
      if (key && !byMcp.has(key)) byMcp.set(key, m);
    }
    return Array.from(byMcp.values());
  },

  getMcpsPorGrupo: async (grupoMcp: number): Promise<MedioTransferencia[]> => {
    const token = getAccessToken();
    const mapItem = (it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.nroCuenta ?? it?.cartera ?? idx,
      nombre: it?.banco ?? it?.nombre ?? it?.descripcion ?? it?.detalle ?? `Medio ${idx + 1}`,
      descripcion: it?.descripcion ?? it?.Detalle ?? undefined,
      mcp: it?.mcp ?? it?.MCP ?? undefined,
      clase: it?.clase ?? it?.Clase ?? undefined,
      grupo: it?.grupoMcp ?? it?.grupo ?? undefined,
      nroSuc: it?.nroSuc ?? it?.sucursal ?? undefined,
      tipo: (it?.tipo ?? it?.Tipo ?? '') ? String(it.tipo ?? it.Tipo).trim().toUpperCase() : undefined,
    });

    const allItems: MedioTransferencia[] = [];
    let pageNumber = 1;
    const pageSize = 100;

    for (let i = 0; i < 50; i++) {
      const response = await api.get<any>(`/api/Mcps/por-grupo/${grupoMcp}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        params: { pageNumber, pageSize },
      });
      const data = response.data;
      const items: any[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.value)
        ? data.value
        : [];

      if (items.length === 0) break;
      allItems.push(...items.map(mapItem));

      const totalPages = Number(data?.totalPages ?? data?.TotalPages ?? 0);
      if (totalPages > 0 && pageNumber >= totalPages) break;
      if (items.length < pageSize) break;
      pageNumber += 1;
    }

    return allItems;
  },

  getMediosTransferenciaPorGrupo: async (grupoMcp: number): Promise<MedioTransferencia[]> => {
    const token = getAccessToken();
    const response = await api.get<any>(`/api/Mcps/por-grupo/${grupoMcp}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params: { pageNumber: 1, pageSize: 50 }, // Límite del backend para Mcps
    });
    const data = response.data;
    const items: any[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.value)
      ? data.value
      : [];

    const filtrados = items.filter((it: any) => ((it?.tipo ?? it?.Tipo ?? '') + '').trim().toUpperCase() === 'TB');

    return filtrados.map((it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.nroCuenta ?? it?.cartera ?? idx,
      nombre: it?.nombreBanco ?? it?.banco ?? it?.nombre ?? it?.descripcion ?? it?.detalle ?? `Medio ${idx + 1}`,
      descripcion: it?.descripcion ?? it?.Detalle ?? undefined,
      mcp: it?.mcp ?? it?.MCP ?? undefined,
      clase: it?.clase ?? it?.Clase ?? undefined,
      grupo: it?.grupoMcp ?? it?.grupo ?? undefined,
      nroSuc: it?.nroSuc ?? it?.sucursal ?? undefined,
      tipo: it?.tipo ?? it?.Tipo ?? undefined,
      cartera: it?.cartera ?? it?.Cartera ?? undefined,
      nombreBanco: it?.nombreBanco ?? it?.NombreBanco ?? undefined,
      cbu: it?.cbu ?? it?.Cbu ?? undefined,
      nroDeCuenta: it?.nroDeCuenta ?? it?.NroDeCuenta ?? undefined,
      idCompania: String(it?.idCompania ?? it?.IdCompania ?? '').trim() || undefined,
    }));
  },

  getMediosEfectivoPorGrupo: async (grupoMcp: number): Promise<MedioTransferencia[]> => {
    const token = getAccessToken();
    const response = await api.get<any>(`/api/Mcps/por-grupo/${grupoMcp}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params: { pageNumber: 1, pageSize: 50 }, // Límite del backend para Mcps
    });
    const data = response.data;
    const items: any[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.value)
      ? data.value
      : [];

    const filtrados = items.filter((it: any) => ((it?.tipo ?? it?.Tipo ?? '') + '').trim().toUpperCase() === 'E');

    return filtrados.map((it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.nroCuenta ?? it?.cartera ?? idx,
      nombre: it?.banco ?? it?.nombre ?? it?.descripcion ?? it?.detalle ?? `Medio ${idx + 1}`,
      descripcion: it?.descripcion ?? it?.Detalle ?? undefined,
      mcp: it?.mcp ?? it?.MCP ?? undefined,
      clase: it?.clase ?? it?.Clase ?? undefined,
      grupo: it?.grupoMcp ?? it?.grupo ?? undefined,
      nroSuc: it?.nroSuc ?? it?.sucursal ?? undefined,
      tipo: it?.tipo ?? it?.Tipo ?? undefined,
    }));
  },

  getMediosOtrosPorGrupo: async (grupoMcp: number): Promise<MedioTransferencia[]> => {
    const token = getAccessToken();
    const response = await api.get<any>(`/api/Mcps/por-grupo/${grupoMcp}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params: { pageNumber: 1, pageSize: 50 }, // Límite del backend para Mcps
    });
    const data = response.data;
    const items: any[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.value)
      ? data.value
      : [];

    const filtrados = items.filter((it: any) => ((it?.tipo ?? it?.Tipo ?? '') + '').trim().toUpperCase() === 'O');

    return filtrados.map((it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.nroCuenta ?? it?.cartera ?? idx,
      nombre: it?.banco ?? it?.nombre ?? it?.descripcion ?? it?.detalle ?? `Medio ${idx + 1}`,
      descripcion: it?.descripcion ?? it?.Detalle ?? undefined,
      mcp: it?.mcp ?? it?.MCP ?? undefined,
      clase: it?.clase ?? it?.Clase ?? undefined,
      grupo: it?.grupoMcp ?? it?.grupo ?? undefined,
      nroSuc: it?.nroSuc ?? it?.sucursal ?? undefined,
      tipo: it?.tipo ?? it?.Tipo ?? undefined,
    }));
  },

  getMediosChequesPorGrupo: async (grupoMcp: number): Promise<MedioTransferencia[]> => {
    const token = getAccessToken();
    const response = await api.get<any>(`/api/Mcps/por-grupo/${grupoMcp}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params: { pageNumber: 1, pageSize: 50 }, // Límite del backend para Mcps
    });
    const data = response.data;
    const items: any[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.value)
      ? data.value
      : [];

    const filtrados = items.filter((it: any) => ((it?.tipo ?? it?.Tipo ?? '') + '').trim().toUpperCase() === 'C');

    return filtrados.map((it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.nroCuenta ?? it?.cartera ?? idx,
      nombre: it?.banco ?? it?.nombre ?? it?.descripcion ?? it?.detalle ?? `Medio ${idx + 1}`,
      descripcion: it?.descripcion ?? it?.Detalle ?? undefined,
      mcp: it?.mcp ?? it?.MCP ?? undefined,
      clase: it?.clase ?? it?.Clase ?? undefined,
      grupo: it?.grupoMcp ?? it?.grupo ?? undefined,
      nroSuc: it?.nroSuc ?? it?.sucursal ?? undefined,
      tipo: it?.tipo ?? it?.Tipo ?? undefined,
      cartera: it?.cartera ?? it?.Cartera ?? undefined,
    }));
  },

  /** Medios de cobro/pago tipo P (cheque propio) del grupo. */
  getMediosChequesPropiosPorGrupo: async (grupoMcp: number): Promise<MedioTransferencia[]> => {
    const token = getAccessToken();
    const response = await api.get<any>(`/api/Mcps/por-grupo/${grupoMcp}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params: { pageNumber: 1, pageSize: 50 },
    });
    const data = response.data;
    const items: any[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.value)
      ? data.value
      : [];

    const filtrados = items.filter((it: any) => ((it?.tipo ?? it?.Tipo ?? '') + '').trim().toUpperCase() === 'P');

    return filtrados.map((it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.nroCuenta ?? it?.cartera ?? idx,
      nombre: it?.banco ?? it?.nombre ?? it?.descripcion ?? it?.detalle ?? `Medio ${idx + 1}`,
      descripcion: it?.descripcion ?? it?.Detalle ?? undefined,
      mcp: it?.mcp ?? it?.MCP ?? undefined,
      clase: it?.clase ?? it?.Clase ?? undefined,
      grupo: it?.grupoMcp ?? it?.grupo ?? undefined,
      nroSuc: it?.nroSuc ?? it?.sucursal ?? undefined,
      tipo: it?.tipo ?? it?.Tipo ?? undefined,
      cartera: it?.cartera ?? it?.Cartera ?? undefined,
      nombreBanco: it?.nombreBanco ?? it?.NombreBanco ?? it?.banco ?? undefined,
    }));
  },

  getMediosTarjetaPorGrupo: async (grupoMcp: number): Promise<MedioTransferencia[]> => {
    const token = getAccessToken();
    const response = await api.get<any>(`/api/Mcps/por-grupo/${grupoMcp}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params: { pageNumber: 1, pageSize: 50 }, // Límite del backend para Mcps
    });
    const data = response.data;
    const items: any[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.value)
      ? data.value
      : [];

    const filtrados = items.filter((it: any) => {
      const rawTipo = ((it?.tipo ?? it?.Tipo ?? '') + '').trim().toUpperCase();
      const rawClase = ((it?.clase ?? it?.Clase ?? '') + '').trim().toUpperCase();
      const rawNombre = ((it?.nombre ?? it?.banco ?? it?.descripcion ?? it?.detalle ?? '') + '')
        .trim()
        .toUpperCase();
      const looksLikeTarjeta =
        rawTipo === 'T' ||
        rawTipo === 'TA' ||
        rawTipo === 'TJ' ||
        rawTipo === 'TC' ||
        rawTipo === 'TD' ||
        rawTipo.startsWith('TARJ') ||
        rawClase.includes('TARJ') ||
        rawNombre.includes('TARJ');
      return looksLikeTarjeta;
    });

    return filtrados.map((it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.nroCuenta ?? it?.cartera ?? idx,
      nombre: it?.banco ?? it?.nombre ?? it?.descripcion ?? it?.detalle ?? `Medio ${idx + 1}`,
      descripcion: it?.descripcion ?? it?.Detalle ?? undefined,
      mcp: it?.mcp ?? it?.MCP ?? undefined,
      clase: it?.clase ?? it?.Clase ?? undefined,
      grupo: it?.grupoMcp ?? it?.grupo ?? undefined,
      nroSuc: it?.nroSuc ?? it?.sucursal ?? undefined,
      tipo: it?.tipo ?? it?.Tipo ?? undefined,
    }));
  },

  getMediosTarjetaPorGrupoPaged: async (
    grupoMcp: number,
    options: { pageNumber: number; pageSize: number }
  ): Promise<MedioTransferencia[]> => {
    const token = getAccessToken();
    const response = await api.get<any>(`/api/Mcps/por-grupo/${grupoMcp}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params: { pageNumber: options.pageNumber, pageSize: options.pageSize },
    });
    const data = response.data;
    const items: any[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.value)
      ? data.value
      : [];
    const filtrados = items.filter((it: any) => {
      const rawTipo = ((it?.tipo ?? it?.Tipo ?? '') + '').trim().toUpperCase();
      const rawClase = ((it?.clase ?? it?.Clase ?? '') + '').trim().toUpperCase();
      const rawNombre = ((it?.nombre ?? it?.banco ?? it?.descripcion ?? it?.detalle ?? '') + '')
        .trim()
        .toUpperCase();
      const looksLikeTarjeta =
        rawTipo === 'T' ||
        rawTipo === 'TA' ||
        rawTipo === 'TJ' ||
        rawTipo === 'TC' ||
        rawTipo === 'TD' ||
        rawTipo.startsWith('TARJ') ||
        rawClase.includes('TARJ') ||
        rawNombre.includes('TARJ');
      return looksLikeTarjeta;
    });
    return filtrados.map((it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.nroCuenta ?? it?.cartera ?? idx,
      nombre: it?.banco ?? it?.nombre ?? it?.descripcion ?? it?.detalle ?? `Medio ${idx + 1}`,
      descripcion: it?.descripcion ?? it?.Detalle ?? undefined,
      mcp: it?.mcp ?? it?.MCP ?? undefined,
      clase: it?.clase ?? it?.Clase ?? undefined,
      grupo: it?.grupoMcp ?? it?.grupo ?? undefined,
      nroSuc: it?.nroSuc ?? it?.sucursal ?? undefined,
      tipo: it?.tipo ?? it?.Tipo ?? undefined,
    }));
  },

  getMarcasTarjetas: async (): Promise<MarcaTarjeta[]> => {
    const token = getAccessToken();
    const response = await api.get<any>('/api/MarcasTarjetas', {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      // MarcasTarjetas no tiene paginación, retorna todos los registros
    });
    const data = response.data;
    const items: any[] = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
    const marcas: MarcaTarjeta[] = [];
    for (const it of items) {
      const codigoMarca = Number(it?.codigoMarca ?? it?.CodigoMarca ?? 0);
      if (!Number.isFinite(codigoMarca) || codigoMarca <= 0) continue;
      const nombreMarca = (it?.nombreMarca ?? it?.NombreMarca ?? '').trim();
      const nombre = nombreMarca || (it?.nombre ?? it?.descripcion ?? `Marca ${codigoMarca}`).trim();
      marcas.push({
        codigoMarca,
        id: codigoMarca,
        nombre,
        nombreMarca: nombreMarca || undefined,
      });
    }
    return marcas;
  },

  getBancos: async (searchTerm?: string): Promise<Banco[]> => {
    const token = getAccessToken();
    const response = await api.get<any>('/api/Banco', {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params: {
        pageNumber: 1,
        pageSize: 20, // Límite del backend
        searchTerm: searchTerm || undefined,
      },
    });
    const data = response.data;
    const items: any[] = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
    return items.map((it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.codigoBanco ?? it?.nroBanco ?? idx,
      nombre: it?.nombreBanco ?? it?.nombre ?? it?.descripcion ?? `Banco ${idx + 1}`,
      codigo: it?.codigoBanco ?? it?.codigo ?? it?.id,
      nroBanco: it?.nroBanco ?? it?.codigoBanco ?? it?.codigo ?? it?.id,
    }));
  },

  // Obtiene todos los bancos paginando hasta traerlos completos (sin filtro)
  getBancosAll: async (): Promise<Banco[]> => {
    const token = getAccessToken();
    const pageSize = 20; // Límite del backend
    let pageNumber = 1;
    const acumulados: Banco[] = [];

    // Limitar a 100 páginas como salvaguarda
    for (let i = 0; i < 100; i++) {
      const response = await api.get<any>('/api/Banco', {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        params: {
          pageNumber,
          pageSize,
        },
      });
      const data = response.data;
      const items: any[] = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
      const mapped: Banco[] = items.map((it: any, idx: number) => ({
        id: it?.id ?? it?.codigo ?? it?.codigoBanco ?? it?.nroBanco ?? idx,
        nombre: it?.nombreBanco ?? it?.nombre ?? it?.descripcion ?? `Banco ${idx + 1}`,
        codigo: it?.codigoBanco ?? it?.codigo ?? it?.id,
        nroBanco: it?.nroBanco ?? it?.codigoBanco ?? it?.codigo ?? it?.id,
      }));
      acumulados.push(...mapped);
      if (items.length < pageSize) break;
      pageNumber += 1;
    }

    // Eliminar duplicados por id
    const byId = new Map<number, Banco>();
    for (const b of acumulados) {
      if (!byId.has(b.id)) {
        byId.set(b.id, b);
      }
    }
    return Array.from(byId.values());
  },

  // Paginado simple para infinite scroll
  getBancosPaged: async (options: { searchTerm?: string; pageNumber: number; pageSize: number }): Promise<Banco[]> => {
    const token = getAccessToken();
    const response = await api.get<any>('/api/Banco', {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params: {
        pageNumber: options.pageNumber,
        pageSize: options.pageSize,
        searchTerm: options.searchTerm || undefined,
      },
    });
    const data = response.data;
    const items: any[] = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
    return items.map((it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.codigoBanco ?? it?.nroBanco ?? idx,
      nombre: it?.nombreBanco ?? it?.nombre ?? it?.descripcion ?? `Banco ${idx + 1}`,
      codigo: it?.codigoBanco ?? it?.codigo ?? it?.id,
      nroBanco: it?.nroBanco ?? it?.codigoBanco ?? it?.codigo ?? it?.id,
    }));
  },

  /** Bancos de cartera (para depósito/extracción 55/56). Usa GET /api/CarteraBancos. */
  getCarteraBancosPaged: async (options: { searchTerm?: string; tipoDeCuenta?: string; pageNumber: number; pageSize: number }): Promise<Banco[]> => {
    const token = getAccessToken();
    const response = await api.get<any>('/api/CarteraBancos', {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params: {
        PageNumber: options.pageNumber,
        PageSize: options.pageSize,
        SearchTerm: options.searchTerm || undefined,
        TipoDeCuenta: options.tipoDeCuenta || undefined,
      },
    });
    const data = response.data;
    const items: any[] = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
    return items.map((it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.codigoBanco ?? it?.nroBanco ?? idx,
      nombre: it?.nombreBanco ?? it?.nombre ?? it?.descripcion ?? `Banco ${idx + 1}`,
      codigo: it?.codigoBanco ?? it?.codigo ?? it?.id,
      nroBanco: it?.nroBanco ?? it?.codigoBanco ?? it?.codigo ?? it?.id,
    }));
  },

  // Obtiene nroSuc asociado al grupo MCP consultando los Mcps del grupo y
  // tomando el primer campo numérico que luzca como sucursal
  getNroSucPorGrupo: async (grupoMcp: number): Promise<number | null> => {
    const token = getAccessToken();
    const response = await api.get<any>(`/api/Mcps/por-grupo/${grupoMcp}`, {
      headers: { Authorization: token ? `Bearer ${token}` : undefined },
      params: { pageNumber: 1, pageSize: 50 },
    });
    const data = response.data;
    const items: any[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.value)
      ? data.value
      : [];

    const candidateKeys = [
      'nroSuc', 'NroSuc', 'nrosuc', 'sucursal', 'Sucursal', 'nroSucursal', 'nro_suc', 'suc'
    ];

    for (const it of items) {
      for (const key of candidateKeys) {
        if (key in it) {
          const val = Number(it[key]);
          if (Number.isFinite(val) && val > 0) return val;
        }
      }
    }
    return null;
  },

  // Detalle de un MCP por nombre y tipo (E, C, T, TB, O)
  getMcpDetallePorNombreYTipo: async (
    mcpNombre: string,
    tipo: string
  ): Promise<{ grupo?: number; nroSuc?: number; tipo?: string }> => {
    const token = getAccessToken();
    const encoded = encodeURIComponent(mcpNombre);
    const response = await api.get<any>(`/api/Mcps/${encoded}/${tipo}`, {
      headers: { Authorization: token ? `Bearer ${token}` : undefined },
    });
    const data = response.data || {};
    return {
      grupo: data?.grupoMcp ?? data?.grupo ?? undefined,
      nroSuc: data?.nroSuc ?? data?.sucursal ?? undefined,
      tipo: data?.tipo ?? tipo,
    };
  },

  // Obtener caja abierta por grupo
  getCajaAbiertaPorGrupo: async (numeroGrupo: number): Promise<Caja | null> => {
    const token = getAccessToken();
    try {
      const response = await api.get<any>('/api/Caja', {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        params: {
          Estado: 'ABIERTO',
          Grupo: numeroGrupo,
        },
      });
      const data = response.data;
      // La respuesta puede ser un array o un objeto
      let cajas: any[] = [];
      if (Array.isArray(data)) {
        cajas = data;
      } else if (Array.isArray(data?.items)) {
        cajas = data.items;
      } else if (data && !Array.isArray(data)) {
        // Si es un solo objeto, lo convertimos en array
        cajas = [data];
      }
      
      // Retornar la primera caja abierta encontrada
      if (cajas.length > 0) {
        const caja = cajas[0];
        const codigoCaja = caja.codigoCaja ?? caja.nroCaja ?? caja.codigo ?? caja.id ?? null;
        return {
          nroCaja: codigoCaja,
          codigo: codigoCaja,
          codigoCaja: codigoCaja,
          grupo: caja.grupo ?? numeroGrupo,
          estado: caja.estado ?? 'ABIERTO',
          ...caja,
        };
      }
      return null;
    } catch (error) {
      console.error('Error al obtener caja abierta:', error);
      return null;
    }
  },

  // Obtener cheques de cartera (mismos filtros que informe Cheques en cartera).
  // CodigoCaja es opcional: sin caja se listan cheques del MCP / filtros como en el informe.
  getChequesCartera: async (
    codigoCaja: number | null | undefined,
    pageNumber?: number,
    pageSize?: number,
    filters?: ChequesCarteraQueryParams
  ): Promise<{ cheques: ChequeCartera[]; hasMore: boolean; totalCount?: number }> => {
    const token = getAccessToken();
    const params: Record<string, string | number> = {
      ...(pageNumber != null ? { pageNumber } : {}),
      ...(pageSize != null ? { pageSize } : {}),
    };
    if (codigoCaja != null && Number(codigoCaja) > 0) {
      params.CodigoCaja = Number(codigoCaja);
    }
    const f = filters;
    if (f?.searchTerm?.trim()) params.searchTerm = f.searchTerm.trim();
    if (f?.fechaDesde?.trim()) params.fechaDesde = f.fechaDesde.trim();
    if (f?.fechaHasta?.trim()) params.fechaHasta = f.fechaHasta.trim();
    if (f?.fechaVencimientoDesde?.trim()) params.fechaVencimientoDesde = f.fechaVencimientoDesde.trim();
    if (f?.fechaVencimientoHasta?.trim()) params.fechaVencimientoHasta = f.fechaVencimientoHasta.trim();
    if (f?.nroBanco != null && Number.isFinite(f.nroBanco)) params.nroBanco = f.nroBanco;
    if (f?.nroSucursal != null && Number.isFinite(f.nroSucursal)) params.nroSucursal = f.nroSucursal;
    if (f?.importeDesde != null && Number.isFinite(f.importeDesde)) params.importeDesde = f.importeDesde;
    if (f?.importeHasta != null && Number.isFinite(f.importeHasta)) params.importeHasta = f.importeHasta;
    if (f?.mcp?.trim()) params.mcp = f.mcp.trim();
    if (f?.estado?.trim()) params.estado = f.estado.trim();
    if (f?.librador?.trim()) params.librador = f.librador.trim();
    if (f?.sortBy?.trim()) params.sortBy = f.sortBy.trim();
    if (f?.sortDir === 'asc' || f?.sortDir === 'desc') params.sortDir = f.sortDir;

    const response = await api.get<any>('/api/Tesoreria/cheques-cartera', {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params,
    });
    const data = response.data;
    
    // Intentar diferentes estructuras de respuesta
    let cheques: any[] = [];
    if (Array.isArray(data?.chequesCartera)) {
      cheques = data.chequesCartera;
    } else if (Array.isArray(data)) {
      cheques = data;
    } else if (data?.chequesCartera && Array.isArray(data.chequesCartera)) {
      cheques = data.chequesCartera;
    } else if (data?.items && Array.isArray(data.items)) {
      cheques = data.items;
    }
    
    // Determinar si hay más páginas
    let hasMore = false;
    if (pageNumber && pageSize) {
      const totalCount = data?.totalCount ?? data?.TotalCount;
      const flagHasMore = data?.hasMore ?? data?.HasMore;
      if (typeof flagHasMore === 'boolean') {
        hasMore = flagHasMore;
      } else if (totalCount != null && Number.isFinite(Number(totalCount))) {
        hasMore = Number(totalCount) > pageNumber * pageSize;
      } else {
        // Sin metadatos: solo asumir más páginas si llenó la página pedida
        hasMore = cheques.length >= pageSize;
      }
    }
    
    const chequesMapeados = cheques.map((cheque: any) => ({
      nroCarga: Number(
        cheque.nroCarga ??
          cheque.NroCarga ??
          cheque.nro_carga ??
          cheque.NRO_CARGA ??
          cheque.idCarga ??
          cheque.IdCarga ??
          0
      ) || 0,
      nroBanco: cheque.nroBanco ?? cheque.NroBanco ?? 0,
      nombreBanco: cheque.nombreBanco ?? cheque.NombreBanco ?? '',
      nroSucursal: cheque.nroSucursal ?? cheque.NroSucursal ?? 0,
      nombreSucursal: cheque.nombreSucursal ?? cheque.NombreSucursal ?? '',
      nroCheque: cheque.nroCheque ?? cheque.NroCheque ?? 0,
      nroLibrador: cheque.nroLibrador ?? cheque.NroLibrador ?? '',
      librador: cheque.librador ?? cheque.Librador ?? '',
      importe: cheque.importe ?? cheque.Importe ?? 0,
      fechaIngreso: cheque.fechaIngreso ?? cheque.FechaIngreso ?? '',
      nroMovCajaIngre: cheque.nroMovCajaIngre ?? cheque.NroMovCajaIngre ?? 0,
      nroDestino: cheque.nroDestino ?? cheque.NroDestino ?? 0,
      fechaSalida: cheque.fechaSalida ?? cheque.FechaSalida ?? '',
      nroMovCajaSalida: cheque.nroMovCajaSalida ?? cheque.NroMovCajaSalida ?? 0,
      fechaVencimiento: cheque.fechaVencimiento ?? cheque.FechaVencimiento ?? '',
      concepto: cheque.concepto ?? cheque.Concepto ?? '',
      fechaEmision: cheque.fechaEmision ?? cheque.FechaEmision ?? '',
      conciliado: cheque.conciliado ?? cheque.Conciliado ?? 0,
      asentado: cheque.asentado ?? cheque.Asentado ?? 0,
      mcp: cheque.mcp ?? cheque.Mcp ?? cheque.MCP ?? '',
      nroCuenta: cheque.nroCuenta ?? cheque.NroCuenta ?? '',
      comprobanteIngreso: cheque.comprobanteIngreso ?? cheque.ComprobanteIngreso ?? '',
      comprobanteSalida: cheque.comprobanteSalida ?? cheque.ComprobanteSalida ?? '',
      senial: cheque.senial ?? cheque.Senial,
      esElectronico:
        typeof (cheque.esElectronico ?? cheque.EsElectronico) === 'boolean'
          ? Boolean(cheque.esElectronico ?? cheque.EsElectronico)
          : undefined,
    }));
    
    return {
      cheques: chequesMapeados,
      hasMore,
      totalCount:
        data?.totalCount ?? data?.TotalCount ?? (data?.totalCount === 0 ? 0 : undefined),
    };
  },

  /** Obtiene todos los cheques de cartera paginando (como exportación del informe). */
  getChequesCarteraAll: async (
    codigoCaja: number | null | undefined,
    filters?: ChequesCarteraQueryParams,
    onProgress?: (currentPage: number, totalPages: number) => void
  ): Promise<ChequeCartera[]> => {
    const PAGE_SIZE = 20;
    const MAX_PAGES = 500;
    const allCheques: ChequeCartera[] = [];
    let pageNumber = 1;
    let apiTotalCount = 0;

    while (pageNumber <= MAX_PAGES) {
      const result = await tesoreriaService.getChequesCartera(
        codigoCaja,
        pageNumber,
        PAGE_SIZE,
        filters
      );
      if (result.totalCount != null && Number.isFinite(result.totalCount)) {
        apiTotalCount = result.totalCount;
      }

      if (!result.cheques.length) {
        onProgress?.(Math.max(pageNumber - 1, 1), pageNumber > 1 ? pageNumber - 1 : 1);
        break;
      }

      allCheques.push(...result.cheques);

      const totalPages =
        apiTotalCount > 0 ? Math.max(1, Math.ceil(apiTotalCount / PAGE_SIZE)) : 0;
      onProgress?.(pageNumber, totalPages > 0 ? totalPages : pageNumber);

      if (!result.hasMore) break;
      pageNumber += 1;
    }

    return allCheques;
  },

  getTarjetasCartera: async (
    codigoCaja: number,
    pageNumber?: number,
    pageSize?: number,
    filters?: TarjetasCarteraQueryParams
  ): Promise<{ cupones: CuponCartera[]; hasMore: boolean }> => {
    const token = getAccessToken();
    const params: Record<string, string | number> = {
      CodigoCaja: codigoCaja,
      ...(pageNumber != null ? { pageNumber } : {}),
      ...(pageSize != null ? { pageSize } : {}),
    };
    const f = filters;
    if (f?.searchTerm?.trim()) params.searchTerm = f.searchTerm.trim();
    if (f?.fechaDesde?.trim()) params.fechaDesde = f.fechaDesde.trim();
    if (f?.fechaHasta?.trim()) params.fechaHasta = f.fechaHasta.trim();
    if (f?.fechaPresentarDesde?.trim()) params.fechaPresentarDesde = f.fechaPresentarDesde.trim();
    if (f?.fechaPresentarHasta?.trim()) params.fechaPresentarHasta = f.fechaPresentarHasta.trim();
    if (f?.codigoMarca != null && Number.isFinite(f.codigoMarca)) params.codigoMarca = f.codigoMarca;
    if (f?.importeDesde != null && Number.isFinite(f.importeDesde)) params.importeDesde = f.importeDesde;
    if (f?.importeHasta != null && Number.isFinite(f.importeHasta)) params.importeHasta = f.importeHasta;
    if (f?.mcp?.trim()) params.mcp = f.mcp.trim();

    const response = await api.get<any>('/api/Tesoreria/tarjetas-cartera', {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params,
    });
    const data = response.data;

    let cupones: any[] = [];
    if (Array.isArray(data?.tarjetasCartera)) {
      cupones = data.tarjetasCartera;
    } else if (Array.isArray(data)) {
      cupones = data;
    } else if (data?.items && Array.isArray(data.items)) {
      cupones = data.items;
    }

    let hasMore = false;
    if (pageNumber && pageSize) {
      const totalCount = data?.totalCount ?? data?.TotalCount;
      const flagHasMore = data?.hasMore ?? data?.HasMore;
      if (typeof flagHasMore === 'boolean') {
        hasMore = flagHasMore;
      } else if (totalCount != null && Number.isFinite(Number(totalCount))) {
        hasMore = Number(totalCount) > pageNumber * pageSize;
      } else {
        hasMore = cupones.length >= pageSize;
      }
    }

    const cuponesMapeados: CuponCartera[] = cupones.map((c: any) => ({
      nroCarga: Number(
        c.nroCarga ??
          c.NroCarga ??
          c.nro_carga ??
          c.NRO_CARGA ??
          c.idCarga ??
          c.IdCarga ??
          0
      ) || 0,
      codigoMarca: c.codigoMarca ?? c.CodigoMarca ?? 0,
      nombreMarca: c.nombreMarca ?? c.NombreMarca ?? '',
      nroTarjeta: c.nroTarjeta ?? c.NroTarjeta ?? '',
      numeroroAutorizacion:
        c.numeroroAutorizacion ??
        c.NumeroroAutorizacion ??
        c.numeroAutorizacion ??
        c.NumeroAutorizacion ??
        '',
      nroComprobanteTa: c.nroComprobanteTa ?? c.NroComprobanteTa ?? 0,
      fecha: c.fecha ?? c.Fecha ?? '',
      fechaPresentar: c.fechaPresentar ?? c.FechaPresentar ?? '',
      titularTarjeta: c.titularTarjeta ?? c.TitularTarjeta ?? '',
      telefono: c.telefono ?? c.Telefono ?? '',
      montoOperacion: c.montoOperacion ?? c.MontoOperacion ?? 0,
      nroDestino: c.nroDestino ?? c.NroDestino ?? 0,
      mcp: c.mcp ?? c.Mcp ?? c.MCP ?? '',
      nroIdformulario: c.nroIdformulario ?? c.NroIdformulario ?? 0,
      nroMovCaja: c.nroMovCaja ?? c.NroMovCaja ?? 0,
      nroMovCajaSalida: c.nroMovCajaSalida ?? c.NroMovCajaSalida ?? 0,
      comprobanteIngreso: c.comprobanteIngreso ?? c.ComprobanteIngreso ?? '',
      comprobanteSalida: c.comprobanteSalida ?? c.ComprobanteSalida ?? '',
    }));

    return { cupones: cuponesMapeados, hasMore };
  },

  /** Obtiene todos los cupones de tarjeta de cartera paginando. */
  getTarjetasCarteraAll: async (
    codigoCaja: number,
    filters?: TarjetasCarteraQueryParams,
    onProgress?: (currentPage: number, totalPages: number) => void
  ): Promise<CuponCartera[]> => {
    const PAGE_SIZE = 20;
    const MAX_PAGES = 500;
    const allCupones: CuponCartera[] = [];
    const seen = new Set<number>();
    let pageNumber = 1;

    while (pageNumber <= MAX_PAGES) {
      const result = await tesoreriaService.getTarjetasCartera(
        codigoCaja,
        pageNumber,
        PAGE_SIZE,
        filters
      );

      if (!result.cupones.length) {
        onProgress?.(Math.max(pageNumber - 1, 1), pageNumber > 1 ? pageNumber - 1 : 1);
        break;
      }

      let nuevosEnPagina = 0;
      for (const c of result.cupones) {
        const nro = Number(c.nroCarga) || 0;
        if (nro > 0) {
          if (seen.has(nro)) continue;
          seen.add(nro);
        }
        allCupones.push(c);
        nuevosEnPagina += 1;
      }

      onProgress?.(pageNumber, pageNumber);

      // Sin ítems nuevos (API repitió página) o sin más páginas → cortar
      if (nuevosEnPagina === 0 || !result.hasMore) break;
      pageNumber += 1;
    }

    return allCupones;
  },

  getTransferenciasCaja: async (): Promise<any[]> => {
    const token = getAccessToken();
    const extractItems = (data: any): any[] =>
      Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.value)
        ? data.value
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.data?.items)
        ? data.data.items
        : [];
    try {
      const response = await api.get<any>('/api/TesoreriaCaja/transferencias-caja', {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });
      return extractItems(response.data);
    } catch {
      return [];
    }
  },

  getTransferenciasCajaPaged: async (params: {
    pageNumber: number;
    pageSize: number;
    searchTerm?: string | null;
    fechaDesde?: string | null;
    fechaHasta?: string | null;
    nroSuc?: number;
  }): Promise<{ items: any[]; totalCount: number }> => {
    const token = getAccessToken();
    const extractItems = (data: any): any[] =>
      Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.value)
        ? data.value
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.data?.items)
        ? data.data.items
        : [];
    const getTotalCount = (data: any): number =>
      data?.totalCount ?? data?.total ?? data?.count ?? 0;
    try {
      const apiParams: Record<string, any> = {
        PageNumber: params.pageNumber,
        PageSize: params.pageSize,
      };
      if (params.searchTerm) apiParams.SearchTerm = params.searchTerm;
      if (params.fechaDesde) apiParams.FechaDesde = params.fechaDesde;
      if (params.fechaHasta) apiParams.FechaHasta = params.fechaHasta;
      if (params.nroSuc != null && params.nroSuc > 0) apiParams.NroSuc = params.nroSuc;
      const response = await api.get<any>('/api/TesoreriaCaja/transferencias-caja', {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
        params: apiParams,
      });
      const items = extractItems(response.data);
      const totalCount = getTotalCount(response.data);
      return { items, totalCount: totalCount > 0 ? totalCount : items.length };
    } catch {
      return { items: [], totalCount: 0 };
    }
  },

  postTransferenciaCaja: async (payload: any): Promise<any> => {
    const token = getAccessToken();
    const response = await api.post<any>('/api/TesoreriaCaja/transferencias-caja', payload, {
      headers: { Authorization: token ? `Bearer ${token}` : undefined },
    });
    return response.data;
  },

  puedeAnularRecibo: async (id: number): Promise<{ puedeAnular: boolean; motivos: string[] }> => {
    const response = await api.get<{ puedeAnular: boolean; motivos: string[] }>(
      `/api/Tesoreria/recibos/${id}/puede-anular`
    );
    return response.data;
  },

  anularRecibo: async (id: number, motivo?: string): Promise<void> => {
    await api.post(`/api/Tesoreria/recibos/${id}/anular`, { motivo: motivo ?? null });
  },

  anularOrdenPago: async (id: number): Promise<void> => {
    await api.post(`/api/Tesoreria/ordendepago/${id}/anular`);
  },

  getOrdenPagoEnvioEmailPreview: async (id: number): Promise<OrdenPagoEnvioEmailPreview> => {
    const response = await api.get<OrdenPagoEnvioEmailPreview>(
      `/api/Tesoreria/ordendepago/${id}/envio-email`
    );
    return response.data;
  },

  enviarOrdenPagoPorEmail: async (
    id: number,
    payload: OrdenPagoEnvioEmailRequest
  ): Promise<OrdenPagoEnvioEmailResult> => {
    const response = await api.post<OrdenPagoEnvioEmailResult>(
      `/api/Tesoreria/ordendepago/${id}/enviar-email`,
      payload
    );
    return response.data;
  },

  getOrdenPago: async (id: number): Promise<OrdenPagoDetalleDto> => {
    const paths = [`/api/Tesoreria/ordendepago/${id}/detalle`, `/api/Tesoreria/ordendepago/${id}`];
    const records: Record<string, unknown>[] = [];
    let lastError: unknown;

    for (const path of paths) {
      try {
        const response = await api.get(path);
        records.push(unwrapOrdenPagoPayload(response.data, id));
      } catch (error) {
        lastError = error;
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 404 || status === 405 || path.endsWith('/detalle')) continue;
        throw error;
      }
    }

    let mapped = records.length > 0 ? mapOrdenPagoDetalleFromItem(mergeRecordsPreferringFilled(...records)) : null;

    if (mapped && mapped.nroFormulario > 0) {
      if (mapped.mediosDePago.length === 0) {
        mapped = await enrichMediosFromCaja(mapped, id);
      }
      return mapped;
    }

    const listResponse = await api.get('/api/Tesoreria/ordendepago', {
      params: { pageNumber: 1, pageSize: 20, searchTerm: String(id) },
    });
    const fromList = mapOrdenPagoDetalleDto(listResponse.data, id);
    if (fromList.nroFormulario === id) {
      return fromList.mediosDePago.length === 0 ? enrichMediosFromCaja(fromList, id) : fromList;
    }
    if (lastError) throw lastError;
    throw new Error('No se encontró la orden de pago');
  },

  mapChequePropioDto: (raw: any): ChequePropio => ({
    nroCarga: raw?.nroCarga ?? raw?.NroCarga ?? 0,
    nroBanco: raw?.nroBanco ?? raw?.NroBanco ?? 0,
    nombreBanco: raw?.nombreBanco ?? raw?.NombreBanco ?? '',
    nroSucursal: raw?.nroSucursal ?? raw?.NroSucursal ?? 0,
    nombreSucursal: raw?.nombreSucursal ?? raw?.NombreSucursal ?? '',
    nroCheque: raw?.nroCheque ?? raw?.NroCheque ?? 0,
    nroLibrador: raw?.nroLibrador ?? raw?.NroLibrador ?? 0,
    librador: raw?.librador ?? raw?.Librador ?? '',
    importe: raw?.importe ?? raw?.Importe ?? 0,
    fechaIngreso: raw?.fechaIngreso ?? raw?.FechaIngreso ?? '',
    nroDestino: raw?.nroDestino ?? raw?.NroDestino ?? 0,
    destino: raw?.destino ?? raw?.Destino ?? null,
    fechaVencimiento: raw?.fechaVencimiento ?? raw?.FechaVencimiento ?? '',
    concepto: raw?.concepto ?? raw?.Concepto ?? '',
    nroCuenta: raw?.nroCuenta ?? raw?.NroCuenta ?? null,
    fechaAcred: raw?.fechaAcred ?? raw?.FechaAcred ?? null,
    baja: raw?.baja ?? raw?.Baja ?? null,
    anulado: raw?.anulado ?? raw?.Anulado ?? null,
  }),

  parseChequesPropiosResponse: (data: any, pageNumber: number, pageSize: number): ChequesPropiosPagedResult => {
    let items: any[] = [];
    let count = 0;
    if (data?.chequesPropios && Array.isArray(data.chequesPropios)) {
      items = data.chequesPropios;
      count = data.totalCount ?? data.chequesPropios.length;
    } else if (data?.items && Array.isArray(data.items)) {
      items = data.items;
      count = data.totalCount ?? data.items.length;
    } else if (Array.isArray(data)) {
      items = data;
      count = data.length;
    }
    const chequesPropios = items.map((item) => tesoreriaService.mapChequePropioDto(item));
    return {
      chequesPropios,
      totalCount: count,
      pageNumber: data?.pageNumber ?? pageNumber,
      pageSize: data?.pageSize ?? pageSize,
      totalPages: data?.totalPages,
    };
  },

  buildChequesPropiosParams: (params: ChequesPropiosQueryParams = {}): Record<string, unknown> => {
    const apiParams: Record<string, unknown> = {
      pageNumber: params.pageNumber ?? 1,
      pageSize: Math.min(params.pageSize ?? 20, 20),
    };
    if (params.searchTerm?.trim()) apiParams.searchTerm = params.searchTerm.trim();
    if (params.fechaDesde?.trim()) apiParams.fechaDesde = params.fechaDesde.trim();
    if (params.fechaHasta?.trim()) apiParams.fechaHasta = params.fechaHasta.trim();
    if (params.fechaVencimientoDesde?.trim()) {
      apiParams.fechaVencimientoDesde = params.fechaVencimientoDesde.trim();
    }
    if (params.fechaVencimientoHasta?.trim()) {
      apiParams.fechaVencimientoHasta = params.fechaVencimientoHasta.trim();
    }
    if (params.nroBanco != null && Number.isFinite(params.nroBanco)) apiParams.nroBanco = params.nroBanco;
    if (params.nroSucursal != null && Number.isFinite(params.nroSucursal)) {
      apiParams.nroSucursal = params.nroSucursal;
    }
    if (params.librador?.trim()) apiParams.librador = params.librador.trim();
    if (params.codigoCaja != null && Number(params.codigoCaja) > 0) {
      apiParams.codigoCaja = Number(params.codigoCaja);
    }
    if (params.estado) apiParams.estado = params.estado;
    if (params.sortBy?.trim()) apiParams.sortBy = params.sortBy.trim();
    if (params.sortDir === 'asc' || params.sortDir === 'desc') {
      apiParams.sortDir = params.sortDir;
    }
    return apiParams;
  },

  /**
   * Listado de cheques propios.
   * - pendientes: usa endpoint dedicado (compatible con API actual).
   * - otros estados: GET cheques-propios con query `estado` (contrato backend).
   */
  getChequesPropios: async (params: ChequesPropiosQueryParams = {}): Promise<ChequesPropiosPagedResult> => {
    const pageNumber = params.pageNumber ?? 1;
    const pageSize = Math.min(params.pageSize ?? 20, 20);
    const apiParams = tesoreriaService.buildChequesPropiosParams({ ...params, pageNumber, pageSize });
    const estado = params.estado ?? 'todos';

    const url =
      estado === 'pendientes'
        ? '/api/Tesoreria/cheques-propios-pendientes-acreditar'
        : '/api/Tesoreria/cheques-propios';

    // En pendientes el endpoint ya filtra; no enviar `estado` redundante.
    if (estado === 'pendientes') {
      delete apiParams.estado;
    }

    const response = await api.get(url, { params: apiParams });
    return tesoreriaService.parseChequesPropiosResponse(response.data, pageNumber, pageSize);
  },

  /** POST /api/Tesoreria/cheques-propios/acreditar — impacta/acredita cheques seleccionados. */
  acreditarChequesPropios: async (payload: AcreditarChequesPropiosPayload): Promise<void> => {
    await api.post('/api/Tesoreria/cheques-propios/acreditar', {
      nroCargas: payload.nroCargas,
      fechaAcred: payload.fechaAcred,
    });
  },

  /** POST /api/Tesoreria/cheques-propios/anular — anula cheques propios seleccionados. */
  anularChequesPropios: async (payload: AnularChequesPropiosPayload): Promise<void> => {
    await api.post('/api/Tesoreria/cheques-propios/anular', {
      nroCargas: payload.nroCargas,
      motivo: payload.motivo ?? null,
    });
  },

  /**
   * Indica si ya existe un cheque de tercero no anulado con el mismo banco + N° cheque
   * (incluye cartera y entregados). GET /api/Tesoreria/cheques-terceros
   */
  existeChequeTerceroDuplicado: async (params: {
    nroBanco: number;
    nroCheque: string | number;
    nroSucursal?: number | null;
  }): Promise<{ existe: boolean; nroCarga?: number }> => {
    const nroBanco = Number(params.nroBanco);
    const nroCheque = Number(String(params.nroCheque).trim());

    if (!Number.isFinite(nroBanco) || !Number.isFinite(nroCheque)) {
      return { existe: false };
    }

    const parseItems = (data: any): any[] => {
      if (Array.isArray(data?.chequesTerceros)) return data.chequesTerceros;
      if (Array.isArray(data?.items)) return data.items;
      if (Array.isArray(data)) return data;
      return [];
    };

    const esAnulado = (raw: any) => {
      const senial = raw?.senial ?? raw?.Senial;
      const concepto = String(raw?.concepto ?? raw?.Concepto ?? '');
      return senial === 0 || concepto.toUpperCase().includes('ANULADO');
    };

    const coincide = (raw: any) => {
      const cBanco = Number(raw?.nroBanco ?? raw?.NroBanco);
      const cNro = Number(raw?.nroCheque ?? raw?.NroCheque);
      if (esAnulado(raw)) return false;
      return cBanco === nroBanco && cNro === nroCheque;
    };

    const response = await api.get<any>('/api/Tesoreria/cheques-terceros', {
      params: {
        pageNumber: 1,
        pageSize: 100,
        nroBanco,
        searchTerm: String(params.nroCheque).trim(),
      },
    });
    let match = parseItems(response.data).find(coincide);

    if (!match) {
      const response2 = await api.get<any>('/api/Tesoreria/cheques-terceros', {
        params: {
          pageNumber: 1,
          pageSize: 100,
          nroBanco,
        },
      });
      match = parseItems(response2.data).find(coincide);
    }

    if (!match) return { existe: false };
    return {
      existe: true,
      nroCarga: Number(match.nroCarga ?? match.NroCarga) || undefined,
    };
  },

  getChequesTerceros: async (params: {
    pageNumber?: number;
    pageSize?: number;
    estado?: string;
    nroCliente?: number;
    fechaRechazoDesde?: string;
    fechaRechazoHasta?: string;
    searchTerm?: string;
    nroBanco?: number;
  }): Promise<{ items: import('../pages/tesoreria/chequesTercero/types').ChequeTercero[]; totalCount: number }> => {
    const response = await api.get('/api/Tesoreria/cheques-terceros', {
      params: {
        pageNumber: params.pageNumber ?? 1,
        pageSize: params.pageSize ?? 20,
        ...(params.estado ? { estado: params.estado } : {}),
        ...(params.nroCliente ? { nroCliente: params.nroCliente } : {}),
        ...(params.fechaRechazoDesde ? { fechaRechazoDesde: params.fechaRechazoDesde } : {}),
        ...(params.fechaRechazoHasta ? { fechaRechazoHasta: params.fechaRechazoHasta } : {}),
        ...(params.searchTerm ? { searchTerm: params.searchTerm } : {}),
        ...(params.nroBanco ? { nroBanco: params.nroBanco } : {}),
      },
    });
    const data = response.data as Record<string, unknown>;
    let items: import('../pages/tesoreria/chequesTercero/types').ChequeTercero[] = [];
    if (Array.isArray(data.chequesTerceros)) {
      items = data.chequesTerceros as import('../pages/tesoreria/chequesTercero/types').ChequeTercero[];
    } else if (Array.isArray(data.items)) {
      items = data.items as import('../pages/tesoreria/chequesTercero/types').ChequeTercero[];
    } else if (Array.isArray(data)) {
      items = data as import('../pages/tesoreria/chequesTercero/types').ChequeTercero[];
    }
    const totalCount = Number(data.totalCount ?? items.length);
    return { items, totalCount };
  },

  previewRechazoChequeTercero: async (
    nroCarga: number,
    recargos?: ChequeTerceroRechazoRecargo[]
  ): Promise<ChequeTerceroRechazoPreview> => {
    if (recargos && recargos.length > 0) {
      const payload = recargos.map(({ codigoArticulo, descripcion, importe }) => ({
        codigoArticulo,
        descripcion,
        importe,
      }));
      const response = await api.post<ChequeTerceroRechazoPreview>(
        `/api/Tesoreria/cheques-terceros/${nroCarga}/rechazo/preview`,
        { recargos: payload }
      );
      return response.data;
    }
    const response = await api.get<ChequeTerceroRechazoPreview>(
      `/api/Tesoreria/cheques-terceros/${nroCarga}/rechazo/preview`
    );
    return response.data;
  },

  rechazarChequeTercero: async (
    nroCarga: number,
    payload: ChequeTerceroRechazoRequest
  ): Promise<{ status: number; data: ChequeTerceroRechazoResult }> => {
    const body = {
      ...payload,
      recargos: payload.recargos?.map(({ codigoArticulo, descripcion, importe }) => ({
        codigoArticulo,
        descripcion,
        importe,
      })),
    };
    try {
      const response = await api.post<ChequeTerceroRechazoResult>(
        `/api/Tesoreria/cheques-terceros/${nroCarga}/rechazar`,
        body
      );
      return { status: response.status, data: response.data };
    } catch (e: any) {
      const status = e?.response?.status ?? 500;
      const data = e?.response?.data as ChequeTerceroRechazoResult | undefined;
      if (data && typeof data === 'object') {
        return { status, data };
      }
      throw e;
    }
  },

  getDocumentosRechazoChequeTercero: async (
    nroCarga: number
  ): Promise<ChequeTerceroRechazoDocumentos> => {
    const response = await api.get(
      `/api/Tesoreria/cheques-terceros/${nroCarga}/rechazo/documentos`
    );
    const data = response.data as unknown as Record<string, unknown>;
    const rawDocs = (data.documentos ?? data.Documentos ?? []) as Record<string, unknown>[];
    return {
      nroCarga: Number(data.nroCarga ?? data.NroCarga ?? nroCarga),
      fechaRechazo: (data.fechaRechazo ?? data.FechaRechazo ?? null) as string | null,
      documentos: rawDocs.map((d) => ({
        tipo: String(d.tipo ?? d.Tipo ?? ''),
        titulo: String(d.titulo ?? d.Titulo ?? ''),
        nroFormulario: Number(d.nroFormulario ?? d.NroFormulario ?? 0),
        comprobante: (d.comprobante ?? d.Comprobante ?? null) as string | null,
        importe: d.importe != null || d.Importe != null
          ? Number(d.importe ?? d.Importe)
          : null,
        fecha: (d.fecha ?? d.Fecha ?? null) as string | null,
        nombreTercero: (d.nombreTercero ?? d.NombreTercero ?? null) as string | null,
        cae: (d.cae ?? d.Cae ?? null) as string | null,
      })),
    };
  },

  createRecibo: async (payload: ReciboCreatePayload): Promise<ReciboCreateResponse> => {
    const response = await api.post<ReciboCreateResponse>('/api/Tesoreria/recibos', payload);
    return response.data;
  },
};

export default tesoreriaService;

